import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { FormattedMessage } from 'react-intl';

import Button from '@atlaskit/button/default/button';
import { cssMap, cx } from '@atlaskit/css';
import Heading from '@atlaskit/heading/heading';
import AiGenerativeTextSummaryIcon from '@atlaskit/icon/core/ai-generative-text-summary';
import EyeOpenStrikethroughIcon from '@atlaskit/icon/core/eye-open-strikethrough';
import StatusVerifiedIcon from '@atlaskit/icon/core/status-verified';
import { SmartCardProvider } from '@atlaskit/link-provider/smart-card-provider';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import UFOCustomCohortData from '@atlaskit/react-ufo/custom-cohort-data';
import UFOCustomData from '@atlaskit/react-ufo/custom-data';
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';
import UFOSegment from '@atlaskit/react-ufo/ufo-segment';
import { token } from '@atlaskit/tokens';
import { AuthoritativeAnswerAttribution } from '@atlassian/rovo-platform-ui-components/authoritative-answer-attribution';
import { buildAuthoritativeAnswerPanelShownEvent } from '@atlassian/rovo-platform-ui-components/authoritative-answer-attribution/authoritative-answer-panel-shown-event';
import {
	AUTHORITY_FEEDBACK_ORIGIN_SAIN,
	FEEDBACK_ENTRYPOINT_ID_SAIN,
	submitAuthorityFeedback,
} from '@atlassian/rovo-platform-ui-components/authoritative-answer-attribution/submit-authority-feedback';
import { LinkList } from '@atlassian/smart-knowledge-cards/link-list';

import { AIAnswerError } from '../../ai-answer-dialog/ai-answer-body/ai-answer-result/ai-answer-error';
import { aiAnswerCollapseClicked } from '../../ai-answer-dialog/ai-answer-collapse-clicked';
import { useAIAnswerContext } from '../../ai-answer-dialog/ai-answer-context';
import { aiAnswerExpandClicked } from '../../ai-answer-dialog/ai-answer-expand-clicked';
import { FollowUpSuggestionFooter } from '../../ai-answer-dialog/ai-answer-footer/followup-suggestion-footer';
import { getSearchAIAnalyticsContextData } from '../../ai-answer-dialog/analytics';
import { onAIAnswerResultCompleteShown } from '../../ai-answer-dialog/on-ai-answer-result-complete-shown';
import { onAIAnswerResultFirstByteShown } from '../../ai-answer-dialog/on-ai-answer-result-first-byte-shown';
import { onAuthoritativeSourceClick } from '../../ai-answer-dialog/on-authoritative-source-click';
import { SearchAIAnalyticsContext } from '../../ai-answer-dialog/search-ai-analytics-context';
import { SearchAIAnalyticsContextLocation } from '../../ai-answer-dialog/search-ai-analytics-context-location';
import { type NLPSearchResultSource, type SourceSmartCardType } from '../../ai-answer-dialog/types';
import { SainAction } from '../../common/controller/analytics/hooks/sain-action';
import { useSAINUsageCriteriaMet } from '../../common/controller/analytics/hooks/use-sain-usage';
import { PeopleTeamsAnalyticsListener } from '../../common/controller/analytics/people-teams-analytics-listener';
import { useAnalytics } from '../../common/controller/analytics/use-analytics';
import { AISourcesCarousel } from '../../common/ui/ai-sources';
import { useKnowledgeSmartCardAnalytics } from '../../common/ui/knowledge-smart-card/hooks/use-knowledge-smart-card-analytics';
import { SourcesBar } from '../../common/ui/sources-button/sources-bar';
import { SourcesPopupWithBarTrigger } from '../../common/ui/sources-popup/SourcesPopupWithBarTrigger';
import { SourcesSidePanel } from '../../common/ui/sources-side-panel/sources-side-panel';
import { SAINFeatureControl } from '../../common/utils/sain-feature-control';
import type { QueryIntent } from '../../constants';
import { extractUrlHostname } from '../../extract-url-hostname';
import { useBufferedContent } from '../common/hooks/use-buffered-content';
import { useContentTruncationDetector } from '../common/hooks/use-content-truncation-detector';
import { useDelayedResizeObserver } from '../common/hooks/use-delayed-resize-observer';
import { AICollapse } from '../common/ui/ai-collapse';
import { AIDisclaimerAndFeedback } from '../common/ui/ai-disclaimer-and-feedback';
import { AnimatedSingleLineShowMore } from '../common/ui/animated-single-line-show-more';
import { OpenChatInputBox } from '../common/ui/open-chat-input-box';
import { ReactRenderer } from '../common/ui/react-renderer';
import { SmartAnswerCardBodyGenerating } from '../common/ui/smart-answer-card-body-generating';
import { messages } from './messages';
import { onAIAnswerMinimizedCollapseClicked } from './minimized/analytics/on-ai-answer-minimized-collapse-clicked';
import { onAIAnswerMinimizedExpandClicked } from './minimized/analytics/on-ai-answer-minimized-expand-clicked';
import { onAIAnswerMinimizedScrollUpClicked } from './minimized/analytics/on-ai-answer-minimized-scroll-up-clicked';
import { SmartAnswerCardMinimizedLoadingV2 } from './minimized/loading/smart-answer-card-minimized-loading-v2';
import { SmartAnswerReady } from './minimized/smart-answer-ready';

// Memoized renderer component outside main component
const MemoizedRenderer = React.memo(function MemoizedRenderer({
	contentToRender,
	showAnimation,
}: {
	contentToRender: string;
	showAnimation: boolean;
}) {
	return (
		<ReactRenderer
			key="stable-renderer"
			markdown={contentToRender}
			animateComponents={showAnimation}
		/>
	);
});

function getPerformanceTiming(): number | undefined {
	const perf = (globalThis as { performance?: Performance }).performance;
	if (typeof perf?.now !== 'function') {
		return undefined;
	}

	const now = perf.now();
	return Number.isFinite(now) && now >= 0 ? now : undefined;
}

const styles = cssMap({
	// Force the smart answer card to be the full max width of
	// the search page layout body main content container
	fullWidth: {
		width: '100vw',
		maxWidth: '100%',
		marginBottom: token('space.150'),
	},
	interactive: {
		cursor: 'pointer',
	},
	fullHeight: {
		height: '100%',
	},
	followUpChatInputSectionStyles: {
		marginTop: token('space.200'),
	},
	followUpChatInputSectionWithBarStyles: {
		marginTop: token('space.100'),
	},
	disclaimerWrapperStyles: {
		paddingBottom: token('space.150'),
		paddingLeft: token('space.200'),
		paddingTop: token('space.050'),
		borderTopStyle: 'solid',
		borderTopColor: token('color.border'),
		borderTopWidth: token('border.width'),
	},
	sectionContainer: {
		paddingBottom: token('space.150'),
		paddingLeft: token('space.200'),
		paddingRight: token('space.200'),
		paddingTop: token('space.050'),
	},
	sourcesTopPadding: {
		paddingTop: token('space.050'),
	},
	linkListContainer: {
		marginTop: token('space.075'),
	},
	nonInteractive: {
		pointerEvents: 'none',
	},
	loadingPaddingOverrides: {
		paddingLeft: token('space.150'),
		paddingRight: token('space.150'),
	},
	standardSAINMarginOverrides: {
		marginBottom: token('space.100'),
	},
});

type SmartAnswerCardConsolidatedProps = {
	onFMPReached?: () => void;
	onAnswerExpandedChange?: (expanded: boolean) => void;
	queryIntent?: QueryIntent;
	/**
	 * Called whenever the answer is minimized (hidden via the "Hide answer" button)
	 * or un-minimized (re-shown via the SmartAnswerReady component). Used by the
	 * parent to gate dwell tracking so a minimized answer is not counted as
	 * "viewed". Optional so existing callers are unaffected.
	 */
	onAnswerMinimizedChange?: (minimized: boolean) => void;
	/**
	 * Ref to the portal target (rendered by the search-page layout) where the
	 * floating SmartAnswerReady banner is displayed at the top of the search
	 * results when the user scrolls the answer card out of view.
	 */
	floatingSmartAnswerReadyPortalRef?: React.RefObject<HTMLDivElement>;
};

function SmartAnswerCardConsolidatedComponent({
	onFMPReached,
	onAnswerExpandedChange,
	queryIntent,
	onAnswerMinimizedChange,
	floatingSmartAnswerReadyPortalRef,
}: SmartAnswerCardConsolidatedProps) {
	const [collapsed, setCollapsed] = useState(true);
	// Tracks whether the answer is currently "minimized" — i.e. the loaded answer
	// is hidden and the SmartAnswerReady component is shown in its place. Only
	// flips to true post-load when the user explicitly hides the answer.
	const [isAnswerMinimized, setIsAnswerMinimized] = useState(false);
	const containerRef = useRef<HTMLDivElement>(null);
	const [didUserTriggerCollapse, setDidUserTriggerCollapse] = useState(false);
	const [isVisibleTextComplete, setIsVisibleTextComplete] = useState(false);
	const [showAnimation, setShowAnimation] = useState(false);
	// Tracks whether the answer card is currently within the viewport.
	// Used to decide whether to render the SmartAnswerReady banner at the top of the
	// search results when the user has scrolled the answer card out of view.
	const [isCardInViewport, setIsCardInViewport] = useState(true);

	const componentRenderTimeRef = useRef<number>(Date.now());
	const isVisibleTextCompletionTimingEnabled = fg('kd_sain_visible_text_completion_timing');
	const firstByteEventFiredRef = useRef(false);

	const context = useAIAnswerContext();
	const { commonAttributes, hasError, isAnswered, loading, partialResponse, luminaContentMap } =
		context;
	const { queryHash, searchSessionId, answerString, sources } = commonAttributes;
	const [fireAnalyticsEvent] = useAnalytics();
	const { fireSAINUsageEvent } = useSAINUsageCriteriaMet();
	const streamingText = partialResponse?.partialNlpResult ?? '';
	const [streamingTextToDisplay, setStreamingTextToDisplay] = useState<string>(streamingText);

	// Refs for content truncation detection
	const isVisibleTextCompleteRef = useRef(false);
	const visibleTextCompletionRecordedRef = useRef(false);
	const [visibleTextCompletionData, setVisibleTextCompletionData] = useState<
		| ({
				visibleTextCompletionSource: 'overflow' | 'loading-complete';
				visibleTextCompletionMs: number;
				visibleTextDisplayedLength: number;
				visibleTextEverStreamed: boolean;
		  } & (
				| {
						visibleTextCompletionPerformanceNowMs: number;
						visibleTextCompletionTimingVersion: 1;
				  }
				| {
						visibleTextCompletionPerformanceNowMs?: never;
						visibleTextCompletionTimingVersion?: never;
				  }
		  ))
		| null
	>(null);
	const contentToRenderRef = useRef('');
	const visibleTextEverStreamedRef = useRef(false);
	// Measures only answer text height (excludes sources and chat input) so onReadMoreButtonShown
	// fires only when the text itself is truncated, not due to other content
	const textContentRef = useRef<HTMLDivElement>(null);
	const completeAnswerAnalyticsEventFiredRef = useRef(false);
	const renderedContentRef = useRef<HTMLDivElement>(null);
	const contentContainerRef = useRef<HTMLDivElement>(null);

	const hasLinksInAnswer =
		!!(luminaContentMap && Object.keys(luminaContentMap).length > 0) &&
		fg('kd_sain_enable_inline_lumina');

	const recordVisibleTextCompletion = useCallback(
		(source: 'overflow' | 'loading-complete') => {
			if (visibleTextCompletionRecordedRef.current) {
				return;
			}

			const performanceTiming = isVisibleTextCompletionTimingEnabled
				? getPerformanceTiming()
				: undefined;
			const performanceTimingData =
				performanceTiming !== undefined
					? {
							visibleTextCompletionPerformanceNowMs: performanceTiming,
							visibleTextCompletionTimingVersion: 1 as const,
						}
					: {};
			visibleTextCompletionRecordedRef.current = true;
			setVisibleTextCompletionData({
				visibleTextCompletionSource: source,
				visibleTextCompletionMs: Date.now() - componentRenderTimeRef.current,
				...performanceTimingData,
				visibleTextDisplayedLength: contentToRenderRef.current.length,
				visibleTextEverStreamed: visibleTextEverStreamedRef.current,
			});
		},
		[isVisibleTextCompletionTimingEnabled],
	);

	useEffect(() => {
		if (!visibleTextEverStreamedRef.current && (loading || partialResponse)) {
			visibleTextEverStreamedRef.current = true;
		}
	}, [loading, partialResponse]);

	const bufferedContent = useBufferedContent(streamingTextToDisplay, isAnswered && !loading);
	const contentToRender = isAnswered ? answerString : bufferedContent;

	useEffect(() => {
		contentToRenderRef.current = contentToRender;
	}, [contentToRender]);

	useEffect(() => {
		// If streaming stopped but buffered content still exists, clear it to reset the display
		if (!streamingText && streamingTextToDisplay && fg('kd_fix_abort_errors')) {
			setStreamingTextToDisplay('');
			setIsVisibleTextComplete(false);
			return;
		}
		if (collapsed && !isVisibleTextComplete) {
			setStreamingTextToDisplay(streamingText);
		} else if (!collapsed) {
			setStreamingTextToDisplay(streamingText);
		}
	}, [collapsed, isVisibleTextComplete, streamingText, streamingTextToDisplay]);

	// Notify context when collapsed state changes
	useEffect(() => {
		onAnswerExpandedChange?.(!collapsed);
	}, [collapsed, onAnswerExpandedChange]);

	useEffect(() => {
		if (!loading && !hasError && !isVisibleTextComplete) {
			recordVisibleTextCompletion('loading-complete');
			setIsVisibleTextComplete(true);
			onFMPReached?.(); // Mark FMP as reached for cached answers
		}
	}, [loading, hasError, isVisibleTextComplete, onFMPReached, recordVisibleTextCompletion]);

	// Track if we should show animations (if loading or partialResponse are true)
	useEffect(() => {
		if (!showAnimation && loading && partialResponse) {
			setShowAnimation(true);
		}
		if (loading && !partialResponse && showAnimation && fg('kd_fix_abort_errors')) {
			setShowAnimation(false);
			firstByteEventFiredRef.current = false;
			completeAnswerAnalyticsEventFiredRef.current = false;
		}
	}, [loading, partialResponse, showAnimation]); // use useEffect to trigger analytics event in order to avoid needing to
	// pass commonAttributes as a dependency to handleExpandCollapseButtonClick
	// and thus avoid re-rendering SmartAnswerCardBodyStreaming with every
	// update to commonAttributes (which gets updated with the streamed text)
	useEffect(() => {
		if (didUserTriggerCollapse) {
			fireAnalyticsEvent(aiAnswerCollapseClicked(commonAttributes));
		}
	}, [didUserTriggerCollapse, commonAttributes, fireAnalyticsEvent]);

	// Detect if content is truncated when collapsed
	const isContentTruncated = useContentTruncationDetector(
		renderedContentRef,
		contentContainerRef,
		collapsed,
		[streamingText],
	);

	const isTextTruncated = useContentTruncationDetector(
		textContentRef,
		contentContainerRef,
		collapsed,
		[streamingText],
	);

	// Complete answer analytics
	useEffect(() => {
		// Only fire once and ensure DOM element is ready with meaningful content
		if (completeAnswerAnalyticsEventFiredRef.current || !renderedContentRef.current) {
			return;
		}
		const answerHeight = renderedContentRef.current.getBoundingClientRect().height;
		const containerHeight = contentContainerRef.current?.getBoundingClientRect().height ?? 0;
		const whitespaceHeight = Math.max(containerHeight - answerHeight, 0);

		// Only fire if we have meaningful height (content is rendered)
		if (answerHeight > 0 && containerHeight > 0) {
			fireAnalyticsEvent(
				onAIAnswerResultCompleteShown({
					...commonAttributes,
					answerHeight,
					containerHeight,
					whitespaceHeight,
					queryIntent: queryIntent,
				}),
			);
			completeAnswerAnalyticsEventFiredRef.current = true;
		}
	}, [fireAnalyticsEvent, commonAttributes, queryIntent, isContentTruncated]);

	// SAIN usage event
	useEffect(() => {
		fireSAINUsageEvent({ commonAttributes });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Notify parent (ai-answer-dialog) whenever the "answer minimized" state
	// changes so dwell tracking can be gated on whether the user can actually see
	// the answer.
	useEffect(() => {
		onAnswerMinimizedChange?.(isAnswerMinimized);
	}, [isAnswerMinimized, onAnswerMinimizedChange]);

	// Toggles the answer minimized state for the "Hide answer" button and the
	// in-flow "Expand" (SmartAnswerReady) affordance. `minimized=true` hides the
	// answer behind the SmartAnswerReady component; `minimized=false` re-shows the
	// answer. Fires the corresponding analytics event and updates the state.
	// NOTE: unlike SmartAnswerCardMinimized, this intentionally does NOT touch
	// `userExpandedDuringLoading`/`collapsed` — those are loading-time concerns
	// that don't apply to the consolidated card's minimize flow.
	const handleAnswerHiddenToggle = useCallback(
		(minimized: boolean) => {
			if (minimized) {
				fireAnalyticsEvent(onAIAnswerMinimizedCollapseClicked(commonAttributes));
			} else {
				fireAnalyticsEvent(onAIAnswerMinimizedExpandClicked(commonAttributes));
			}
			setIsAnswerMinimized(minimized);
		},
		[commonAttributes, fireAnalyticsEvent],
	);

	const handleExpandCollapseButtonClick = useCallback(() => {
		if (collapsed) {
			setCollapsed(false);
			setDidUserTriggerCollapse(false);
		} else {
			setCollapsed(true);
			setDidUserTriggerCollapse(true);
		}
	}, [collapsed]);

	// Scrolls the answer card back into view and expands it fully when the user
	// clicks "Scroll up" on the floating SmartAnswerReady banner.
	const handleScrollUpClick = useCallback(() => {
		fireAnalyticsEvent(onAIAnswerMinimizedScrollUpClicked(commonAttributes));
		fireAnalyticsEvent(aiAnswerExpandClicked(commonAttributes));
		setCollapsed(false);
		setDidUserTriggerCollapse(false);
		const node = containerRef.current;
		if (!node) {
			return;
		}
		// The "Scroll up" button only appears on the floating banner, which is only
		// rendered when the card is scrolled out of view (isCardInViewport === false).
		// So reaching this handler means the card is effectively out of view (or hidden
		// behind the fixed banner) and we should always scroll it back to the top.
		//
		// We intentionally avoid an isOutOfViewport short-circuit here: the previous
		// `rect.bottom <= 0` guard failed the edge case where the card's bottom edge
		// sits a few pixels below the viewport top (positive rect.bottom) but the card
		// is still visually hidden behind the fixed floating banner — in that case the
		// guard skipped the scroll and the user was never taken back to the answer.
		node.scrollIntoView({ behavior: 'smooth', block: 'start' });
	}, [commonAttributes, fireAnalyticsEvent]);

	// Track whether the answer card is in the viewport so we can render
	// SmartAnswerReady at the top of the search results when the user has scrolled
	// past the answer card. Only observe once the answer has finished loading and
	// is not in an error/no-answer state. Gated on the scroll-up feature since this
	// observer only exists to drive the floating SmartAnswerReady banner. Mirrors the
	// OR gating in smart-answers/index.tsx so the observer runs whenever the floating
	// portal ref is provided (either the scroll-up gate OR the broader updated-flow
	// experiment is on).
	useEffect(() => {
		if (
			!SAINFeatureControl.isSAINScrollUpEnabled() &&
			!SAINFeatureControl.isSAINUpdatedFlowEnabled()
		) {
			return;
		}
		if (!isAnswered || hasError) {
			return;
		}
		if (!containerRef.current) {
			return;
		}
		const observer = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					setIsCardInViewport(entry.isIntersecting);
				});
			},
			{ threshold: 0 },
		);
		observer.observe(containerRef.current);
		return () => {
			observer.disconnect();
		};
	}, [isAnswered, hasError]);

	const handleVisibleTextComplete = useCallback(() => {
		setIsVisibleTextComplete(true);
		onFMPReached?.(); // Mark FMP as reached for streaming answers
	}, [onFMPReached]);

	const handleContainerClick = useCallback(
		(event: React.MouseEvent<HTMLDivElement>) => {
			const selection = window.getSelection()?.toString() ?? '';
			// Highlighting text should not expand the card
			const hasHighlightedText = selection.length > 0;
			// Clicking in a feedback modal or flag should not expand the card
			const isClickingOnContainer = containerRef.current?.contains(event.target as Node) ?? false;

			if (collapsed && !hasHighlightedText && isClickingOnContainer) {
				setCollapsed(false);
				fireAnalyticsEvent(aiAnswerExpandClicked(commonAttributes));
				fireSAINUsageEvent({ metCriteria: SainAction.EXPAND_CLICKED, commonAttributes });
			}
		},
		[collapsed, fireAnalyticsEvent, commonAttributes, fireSAINUsageEvent],
	);

	const { handleSmartCardClick } = useKnowledgeSmartCardAnalytics({
		aiAnswerDialogContext: context,
	});

	// UFO resize observer for streaming
	const resizeObserverCallback = useCallback(() => {
		if (
			!isVisibleTextCompleteRef.current &&
			renderedContentRef.current &&
			contentContainerRef.current &&
			!!streamingTextToDisplay
		) {
			const renderedContentRefHeight = renderedContentRef.current.getBoundingClientRect().height;
			const contentContainerRefHeight = contentContainerRef.current.getBoundingClientRect().height;
			const isVisibleTextComplete = renderedContentRefHeight > contentContainerRefHeight;
			if (isVisibleTextComplete) {
				recordVisibleTextCompletion('overflow');
				handleVisibleTextComplete();
				isVisibleTextCompleteRef.current = true;
			}
		}
	}, [handleVisibleTextComplete, recordVisibleTextCompletion, streamingTextToDisplay]);
	// eslint-disable-next-line react-hooks/rules-of-hooks
	useDelayedResizeObserver(renderedContentRef, resizeObserverCallback, 100, [
		streamingTextToDisplay,
	]);

	useEffect(() => {
		if (bufferedContent.length > 0 && !firstByteEventFiredRef.current) {
			fireAnalyticsEvent(
				onAIAnswerResultFirstByteShown({
					...commonAttributes,
					timeToFirstByte: Date.now() - componentRenderTimeRef.current,
				}),
			);
			firstByteEventFiredRef.current = true;
		}
	}, [bufferedContent, commonAttributes, fireAnalyticsEvent]);

	const showReadMoreButton = collapsed && isContentTruncated;

	// Single return statement with React component for main content
	const isStreaming = !isAnswered && (loading || loading === undefined);

	// Early return for error state
	if (hasError) {
		return (
			<Box xcss={styles.fullWidth}>
				<AIAnswerError />
			</Box>
		);
	}

	// Keep one UFO segment shell across loading/done states to avoid duplicate `_1`
	// lifecycles in the parent `ufo-unified-search` payload.
	const isGenerating =
		!isAnswered &&
		(loading || loading === undefined) &&
		(!partialResponse || bufferedContent.length < 1);

	const isSAINLoadingStateTracesUpdateEnabled =
		SAINFeatureControl.isSAINLoadingStateTracesUpdateEnabled();

	const visibleTextCompletionSourceData = visibleTextCompletionData ? (
		<UFOCustomData data={visibleTextCompletionData} />
	) : null;

	// Render SmartAnswerReady at the top of the search results when:
	// - the answer has finished loading and is not in an error/no-answer state
	// - the answer card is currently scrolled out of the viewport
	// - the answer is currently in its collapsed state (user hasn't expanded it)
	const notifySmartAnswerReady = isAnswered && !hasError && !isCardInViewport && collapsed;

	const floatingSmartAnswerReadyPortal =
		notifySmartAnswerReady && floatingSmartAnswerReadyPortalRef?.current
			? createPortal(
					<SmartAnswerReady
						appearance="scrollUp"
						onScrollUpClick={handleScrollUpClick}
						testId="smart-answer-ready-floating"
					/>,
					floatingSmartAnswerReadyPortalRef.current,
				)
			: null;

	const answerCardContent = isGenerating ? (
		isSAINLoadingStateTracesUpdateEnabled ? (
			<SmartAnswerCardMinimizedLoadingV2 commonAttributes={commonAttributes} />
		) : (
			<>
				<SmartAnswerCardBodyGenerating />
				<AnimatedSingleLineShowMore isStreaming showMoreButtonVisible={false} />
			</>
		)
	) : (
		<SmartAnswerCardContent
			isStreaming={isStreaming}
			collapsed={collapsed}
			showReadMoreButton={showReadMoreButton}
			handleExpandCollapseButtonClick={handleExpandCollapseButtonClick}
			contentContainerRef={contentContainerRef}
			renderedContentRef={renderedContentRef}
			textContentRef={textContentRef}
			isTextTruncated={isTextTruncated}
			sources={sources}
			searchSessionId={searchSessionId}
			sourceClickCallback={handleSmartCardClick}
			commonAttributes={commonAttributes}
			contentToRender={contentToRender}
			showAnimation={showAnimation}
			onHideAnswer={() => handleAnswerHiddenToggle(true)}
			handleContainerClick={handleContainerClick}
			containerRef={containerRef}
			hasLinksInAnswer={hasLinksInAnswer}
		/>
	);

	// When the user has minimized the answer (clicked "Hide answer" on the loaded
	// card), render the SmartAnswerReady component in place of the full answer.
	// Clicking it re-shows the answer via handleAnswerHiddenToggle(false).
	// No need to also guard on !isGenerating: `isAnswerMinimized` can only become
	// true via the Hide answer button, which is only shown when !collapsed &&
	// !isStreaming (i.e. the answer is already loaded).
	if (isAnswerMinimized) {
		return <SmartAnswerReady onExpandClick={() => handleAnswerHiddenToggle(false)} />;
	}

	return (
		<Box
			xcss={cx(styles.fullWidth, styles.loadingPaddingOverrides)}
			testId="smart-answer-card-consolidated"
		>
			<UFOSegment name="search-ai-dialog-all-text">
				<UFOLoadHold name="search-ai-dialog-all-text-loading" hold={loading} />
				<UFOSegment name="search-ai-dialog-visible-text">
					<UFOLoadHold name="search-ai-dialog-visible-text-loading" hold={!isVisibleTextComplete} />
					{fg('platform_ufo_custom_cohort_data_migration') ? (
						<>
							<UFOCustomCohortData dataKey="queryHash" value={queryHash} />
							<UFOCustomCohortData dataKey="searchSessionId" value={searchSessionId} />
							<UFOCustomCohortData dataKey="queryIntent" value={queryIntent} />
						</>
					) : (
						<UFOCustomData data={{ queryHash, searchSessionId, queryIntent }} />
					)}
					{visibleTextCompletionSourceData}
					{/* Keep the listener below UFOSegments so their identity stays stable. */}
					<PeopleTeamsAnalyticsListener>{answerCardContent}</PeopleTeamsAnalyticsListener>
				</UFOSegment>
			</UFOSegment>
			{floatingSmartAnswerReadyPortal}
		</Box>
	);
}

function SmartAnswerCardContent({
	isStreaming,
	collapsed,
	showReadMoreButton,
	handleExpandCollapseButtonClick,
	contentContainerRef,
	renderedContentRef,
	textContentRef,
	isTextTruncated,
	sources,
	searchSessionId,
	sourceClickCallback,
	commonAttributes,
	contentToRender,
	showAnimation,
	onHideAnswer,
	handleContainerClick,
	containerRef,
	hasLinksInAnswer,
}: {
	isStreaming: boolean;
	collapsed: boolean;
	showReadMoreButton: boolean;
	handleExpandCollapseButtonClick: () => void;
	onHideAnswer?: () => void;
	contentContainerRef: React.RefObject<HTMLDivElement>;
	renderedContentRef: React.RefObject<HTMLDivElement>;
	textContentRef: React.RefObject<HTMLDivElement>;
	isTextTruncated: boolean;
	sources: NLPSearchResultSource[];
	searchSessionId: string;
	sourceClickCallback: ({
		index,
		source,
		hostname,
		sourceCardType,
		isAuxClick,
		isContextMenu,
	}: {
		index: number;
		source: NLPSearchResultSource;
		hostname?: string;
		sourceCardType: SourceSmartCardType;
		isAuxClick?: boolean;
		isContextMenu?: boolean;
	}) => void;
	commonAttributes: any;
	contentToRender: string;
	showAnimation: boolean;
	handleContainerClick: (event: React.MouseEvent<HTMLDivElement>) => void;
	containerRef: React.RefObject<HTMLDivElement>;
	hasLinksInAnswer: boolean;
}) {
	const { authorityUiMetadata } = useAIAnswerContext();
	const { cloudId, queryHash, extraAttributes, source } = commonAttributes;
	const [fireAnalyticsEvent] = useAnalytics();
	const { fireSAINUsageEvent } = useSAINUsageCriteriaMet();
	const isHighlyAuthoritative =
		fg('rovo_authoritative_answer_attribution') && !!authorityUiMetadata?.isHighlyAuthoritative;

	const handleAuthorityPanelShown = useCallback(() => {
		fireAnalyticsEvent(
			buildAuthoritativeAnswerPanelShownEvent({
				product: 'sain',
				source,
				correlationAttributes: {
					cloudId,
					searchSessionId,
					queryHash,
					traceId: extraAttributes?.requestId,
				},
				authorityUiMetadata,
			}),
		);
	}, [
		fireAnalyticsEvent,
		source,
		cloudId,
		searchSessionId,
		queryHash,
		extraAttributes?.requestId,
		authorityUiMetadata,
	]);

	const fireAuthorityLinkClick = useCallback(
		(linkType: 'source' | 'expert') => {
			fireAnalyticsEvent(
				onAuthoritativeSourceClick({
					...commonAttributes,
					linkType,
					authorityUiMetadata,
				}),
			);

			// Credits the click towards SAIN's success metric (SSRv5).
			fireSAINUsageEvent({ metCriteria: SainAction.SOURCE_CLICKED, commonAttributes });
		},
		[fireAnalyticsEvent, commonAttributes, authorityUiMetadata, fireSAINUsageEvent],
	);

	// URL patterns for sources known not to resolve as rich smart cards.
	const UNRESOLVABLE_URL_PATTERNS = ['viewpageattachments.action'];
	const isUnresolvableSource = (url: string) =>
		UNRESOLVABLE_URL_PATTERNS.some((pattern) => url.includes(pattern));

	// Filter sources whose URLs are known not to resolve as rich smart cards.
	// Uses URL pattern matching since the NlpSearchResultType enum does not include 'attachment'.
	// https://switcheroo.atlassian.com/ui/gates/f309f0ca-53a0-4c56-9767-fec898813e99/key/kd_sain_filter_attachment_sources
	const filteredSources = fg('kd_sain_filter_attachment_sources')
		? sources.filter((source) => !isUnresolvableSource(source.url))
		: sources;

	const sourcesSection = (
		<Stack space="space.100">
			{!isStreaming &&
				filteredSources.length > 0 &&
				SAINFeatureControl.isSourcesInShortSAINAnswersEnabled() && (
					<Box xcss={cx(styles.sourcesTopPadding, collapsed && styles.nonInteractive)}>
						{SAINFeatureControl.isLinkListSourcesEnabled() ? (
							<Stack space="space.100" xcss={styles.linkListContainer}>
								<Heading size="xsmall">
									<FormattedMessage {...messages.related_links_title} />
								</Heading>
								<LinkList
									urls={filteredSources.map((source) => source.url)}
									maxSources={5}
									showExpand={false}
									onLinkClick={(url, index, opts) => {
										const source = filteredSources[index];
										if (source) {
											sourceClickCallback({
												index,
												source,
												hostname: extractUrlHostname(url),
												sourceCardType: 'link-list',
												isAuxClick: opts?.isAuxClick,
												isContextMenu: opts?.isContextMenu,
											});
										}
									}}
								/>
							</Stack>
						) : (
							<AISourcesCarousel
								sources={filteredSources}
								searchSessionId={searchSessionId}
								onClickHandler={sourceClickCallback}
							/>
						)}
					</Box>
				)}
		</Stack>
	);

	return (
		// eslint-disable-next-line @atlassian/a11y/interactive-element-not-keyboard-focusable
		<Box
			xcss={cx(collapsed ? styles.interactive : undefined, styles.standardSAINMarginOverrides)}
			onClick={handleContainerClick}
			ref={containerRef}
		>
			<Stack alignBlock="stretch" grow="fill" spread="space-between" xcss={styles.fullHeight}>
				<Stack alignBlock="stretch" grow="fill" spread="space-between" xcss={styles.fullHeight}>
					<Box>
						{/* Conditional UI based on experiments */}
						<AICollapse
							title={
								<Inline
									spread="space-between"
									alignBlock="center"
									grow="fill"
									xcss={styles.standardSAINMarginOverrides}
								>
									<Inline alignBlock="center" space="space.050" shouldWrap>
										{isHighlyAuthoritative && authorityUiMetadata ? (
											<>
												<Inline alignBlock="center" space="space.050">
													<StatusVerifiedIcon
														label=""
														size="medium"
														color={token('color.icon.success')}
													/>
													<SmartCardProvider>
														<AuthoritativeAnswerAttribution
															sources={authorityUiMetadata.sources ?? []}
															domainExperts={authorityUiMetadata.domainExperts ?? []}
															overallReasoning={authorityUiMetadata.overallReasoning}
															labelVariant="answer"
															cloudId={cloudId}
															surface="sain"
															// SAIN renders in the main results column with ample room, so
															// use the taller panel to avoid cramping the feedback form.
															panelSize="tall"
															onPanelOpen={handleAuthorityPanelShown}
															onSourceClick={() => fireAuthorityLinkClick('source')}
															onExpertClick={() => fireAuthorityLinkClick('expert')}
															onFeedbackSubmit={(submission) => {
																// Submit authority-answer feedback to the Feedback Collector API
																// using SAIN's existing entrypoint (same destination as SAIN's
																// footer feedback). Analytics wiring is handled separately.
																void submitAuthorityFeedback({
																	submission,
																	entrypointId: FEEDBACK_ENTRYPOINT_ID_SAIN,
																	origin: AUTHORITY_FEEDBACK_ORIGIN_SAIN,
																	cloudId,
																	debugAttributes: {
																		traceId: extraAttributes?.requestId,
																		searchSessionId,
																		queryHash,
																	},
																});
															}}
														/>
													</SmartCardProvider>
												</Inline>
											</>
										) : (
											<>
												<AiGenerativeTextSummaryIcon label="AI Content" size="medium" />
												<Heading as="h1" size="xsmall">
													<FormattedMessage {...messages.smart_answer_card_dynamic_layout_title} />
												</Heading>
											</>
										)}
									</Inline>
									{/* "Hide answer" button — carried over from SmartAnswerCardMinimized.
										Only rendered behind the SAIN loading state traces update
										gate/experiment, and only once the answer has finished
										streaming. Shown regardless of collapsed/expanded state. */}
									<Inline alignBlock="center" space="space.100">
										{SAINFeatureControl.isSAINLoadingStateTracesUpdateEnabled() &&
											!isStreaming &&
											onHideAnswer && (
												<Button
													spacing="compact"
													appearance="subtle"
													onClick={(e) => {
														// The button lives inside the AICollapse header, which expands
														// the answer when clicked anywhere. Stop propagation so
														// clicking "Hide answer" only hides the answer (and shows
														// SmartAnswerReady) without also expanding it.
														e.stopPropagation();
														onHideAnswer();
													}}
													iconAfter={(iconProps) => (
														<EyeOpenStrikethroughIcon {...iconProps} size="small" />
													)}
												>
													<Text color="color.text.subtlest">
														<FormattedMessage {...messages.hide_answer} />
													</Text>
												</Button>
											)}
									</Inline>
								</Inline>
							}
							isCollapsed={collapsed}
							onExpandCollapse={handleExpandCollapseButtonClick}
							ref={contentContainerRef}
							showReadMoreButton={collapsed}
							luminaAnswerType="STANDARD"
						>
							<Box ref={renderedContentRef} id="ai-answer-result-section" testId="sain-fmp-target">
								<SearchAIAnalyticsContext
									data={getSearchAIAnalyticsContextData({
										location: SearchAIAnalyticsContextLocation.SAIN_Q_AND_A_CARD,
										commonAttributes,
									})}
								>
									<SmartCardProvider>
										<Box ref={textContentRef}>
											<MemoizedRenderer
												contentToRender={contentToRender}
												showAnimation={showAnimation}
											/>
										</Box>
									</SmartCardProvider>
								</SearchAIAnalyticsContext>
								{!hasLinksInAnswer &&
									!fg('kd_team_26_eu_hide_sources_carousel_table') &&
									sourcesSection}
							</Box>
						</AICollapse>

						{(collapsed || showReadMoreButton || isStreaming) && (
							<AnimatedSingleLineShowMore
								isStreaming={isStreaming}
								showMoreButtonVisible={collapsed}
								fireShownEvent={isTextTruncated}
								onClickShowMoreButton={handleExpandCollapseButtonClick}
							/>
						)}
					</Box>

					{/* Sources section - only show when complete and not collapsed */}
					{!isStreaming &&
						!collapsed &&
						filteredSources.length > 0 &&
						!SAINFeatureControl.isSourcesInShortSAINAnswersEnabled() &&
						!hasLinksInAnswer && (
							<Box xcss={styles.sectionContainer}>
								<AISourcesCarousel
									sources={filteredSources}
									searchSessionId={searchSessionId}
									onClickHandler={sourceClickCallback}
								/>
							</Box>
						)}
					{!isStreaming && !collapsed && filteredSources.length > 0 && (
						<Box xcss={styles.followUpChatInputSectionWithBarStyles}>
							{fg('team_26_eu_sain_sources_popup_enable') ? (
								<SourcesPopupWithBarTrigger sources={filteredSources} />
							) : (
								<SourcesSidePanel
									renderTrigger={(onToggle, triggerRef) => (
										<SourcesBar
											sources={filteredSources}
											numSources={filteredSources.length}
											onClick={onToggle}
											triggerRef={triggerRef}
										/>
									)}
								/>
							)}
						</Box>
					)}

					{/* Disclaimer section */}
					{!collapsed && !showReadMoreButton && !isStreaming && (
						<Stack space="space.150" xcss={styles.followUpChatInputSectionStyles}>
							<Inline space="space.100" alignBlock="center">
								<FollowUpSuggestionFooter />
							</Inline>
							<OpenChatInputBox />
							<Box xcss={styles.disclaimerWrapperStyles}>
								{/* textContentRef is used here (not renderedContentRef) so that
									    copied content excludes sources, which sit outside textContentRef */}
								<AIDisclaimerAndFeedback
									showFeedbackButtons={true}
									showCopyButton={!hasLinksInAnswer}
									renderedContentRef={textContentRef}
								/>
							</Box>
						</Stack>
					)}
				</Stack>
			</Stack>
		</Box>
	);
}

export function SmartAnswerCardConsolidated({
	onFMPReached,
	onAnswerExpandedChange,
	queryIntent,
	onAnswerMinimizedChange,
	floatingSmartAnswerReadyPortalRef,
}: SmartAnswerCardConsolidatedProps): React.JSX.Element {
	return (
		<UFOSegment name="smart-answer-card">
			<SmartAnswerCardConsolidatedComponent
				onFMPReached={onFMPReached}
				onAnswerExpandedChange={onAnswerExpandedChange}
				queryIntent={queryIntent}
				onAnswerMinimizedChange={onAnswerMinimizedChange}
				floatingSmartAnswerReadyPortalRef={floatingSmartAnswerReadyPortalRef}
			/>
		</UFOSegment>
	);
}
